import {
  createTheme,
  ThemeProvider,
  StyledEngineProvider,
} from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import Box from '@mui/material/Box'
import { useAppDispatch, useAppSelector } from '../redux/Hook'
import Header from './Header'
import { makeStyles } from '../hooks/makeStyles'
import { Outlet } from 'react-router-dom'
import Notification from '../components/Notification'
import { Suspense } from 'react'
import Loader from '../components/Loader'
import '@mui/x-data-grid/themeAugmentation'

declare module '@mui/material/styles' {
  interface Palette {
    button: Palette['primary']
  }
  interface PaletteOptions {
    button?: PaletteOptions['primary']
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    button: true
  }
}

const useStyles = makeStyles(() => ({
  wrapperContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh', // full viewport height
  },
  contentBoxContainer: {
    flex: 1,
    overflow: 'auto',
    // padding: '1%',
  },
}))

const Wrapper = () => {
  const classes = useStyles()
  const dispatch = useAppDispatch()

  const themeDataState: any = useAppSelector((state) => state.updateTheme)
  const notifyDataState = useAppSelector((state) => state.notificationAlert)

  const theme = createTheme({
    palette: {
      mode: themeDataState?.mode || 'light',
      primary: {
        main:
          themeDataState?.mode === 'light'
            ? themeDataState?.primary
            : themeDataState?.primaryDark,
        light: themeDataState?.primaryLight,
        dark:
          themeDataState?.mode === 'light'
            ? themeDataState?.primaryLightDark
            : themeDataState?.selectionDark || '#1E3A5F', // dark mode: muted selection bg
        contrastText: '#f8f9fa',
        // contrastText: '#000',
      },
      secondary: {
        main: themeDataState?.secondary,
        light: themeDataState?.secondaryLight,
        dark: themeDataState?.secondaryDark,
        contrastText: '#f8f9fa',
      },
      error: {
        main: themeDataState?.error,
        light: themeDataState?.errorLight,
        dark: themeDataState?.errorDark,
        contrastText: '#f8f9fa',
      },
      background: {
        default:
          themeDataState?.mode === 'light'
            ? themeDataState?.background?.default
            : '#0D1117', // dark mode app bg — rich near-black with slight blue tone
        paper:
          themeDataState?.mode === 'light'
            ? themeDataState?.background?.paper
            : '#161B22', // dark mode card/paper — slightly lighter for depth separation
      },
      button: {
        light: themeDataState?.btnLight || '#1f883d',
        dark: themeDataState?.btnDark || '#1f883d',
        main:
          themeDataState?.mode === 'light'
            ? themeDataState?.btnLight
            : themeDataState?.btnDark || '#1f883d',
        contrastText: '#f8f9fa',
      },
      ...(themeDataState?.mode === 'dark' && {
        action: {
          selected: 'rgba(30, 58, 95, 0.6)', // muted navy selection — matches selectionDark
          selectedOpacity: 0.6,
          hover: 'rgba(124, 159, 230, 0.1)', // very subtle blue hover
          hoverOpacity: 0.1,
          focus: 'rgba(124, 159, 230, 0.12)',
          focusOpacity: 0.12,
        },
      }),
    },
    typography: {
      fontFamily:
        // eslint-disable-next-line
        "Roboto, 'Open Sans', 'Helvetica Neue', Arial, Helvetica, sans-serif",
    },
    components: {
      MuiButton: {
        defaultProps: {
          color: 'button', // 👈 all Buttons will use your custom palette
          // variant: 'contained', // 👈 default variant
          size: 'small',
        },
        styleOverrides: {
          root: {
            textTransform: 'none', // 👈 prevent uppercase
            fontWeight: 500,
          },
          /* 🔹 For text variant */
          text: {
            color:
              themeDataState?.mode === 'light'
                ? themeDataState?.primaryLight || '#008eff'
                : themeDataState?.primaryLightDark || '#37AA9C',

            '&:hover': {
              backgroundColor: 'transparent', // optional
            },
          },
          contained: {
            '&:hover': {
              backgroundColor:
                themeDataState?.mode === 'light'
                  ? themeDataState?.btnLight
                  : themeDataState?.btnDark || '#1f883d',
            },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          input: {
            fontSize: 12, // applies to input field
          },
          listbox: {
            fontSize: 14, // applies to menu list
          },
          option: {
            fontSize: 14, // applies to each option
          },
          // popupIndicator: {
          //   fontSize: '18px !important',
          // },
          // clearIndicator: {
          //   fontSize: 18,
          // },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            '&.Mui-disabled': {
              WebkitTextFillColor:
                themeDataState?.mode === 'light' ? 'black' : '#ffffff',
            },
            fontSize: 12, // applies to text input
          },
          root: {
            fontSize: 12,
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            backgroundColor:
              themeDataState.mode === 'light'
                ? themeDataState?.background?.default
                : '#0D1117', // match dark mode app background
          },
          filterForm: {
            '& .MuiInputBase-root': {
              '&.MuiOutlinedInput-root': {
                '& > fieldset': {
                  border: 'none !important',
                },
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
                  transition:
                    'border-bottom-color 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                },
                '&:hover:not(.Mui-disabled):before': {
                  borderBottom: '2px solid rgba(0, 0, 0, 0.87)',
                },
                '&.Mui-focused:after': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderBottom: `2px solid ${themeDataState?.mode === 'light' ? themeDataState?.primary : themeDataState?.primaryDark}`,
                  transform: 'scaleX(1)',
                  transition:
                    'transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
                },
              },
            },
          },
        },
      },
      // MuiFormHelperText: {
      //   styleOverrides: {
      //     root: {
      //       fontSize: 12,
      //     },
      //   },
      // },
    },
  })

  const resetNotificationData = () => {
    dispatch({
      type: 'SET_ALERT_ENABLE',
      enable: false,
    })
  }

  return (
    <StyledEngineProvider injectFirst>
      <Notification
        open={notifyDataState.enable}
        type={notifyDataState.type}
        message={notifyDataState.message}
        duration={notifyDataState.duration}
        setOpen={() => resetNotificationData()}
      />
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box className={classes.wrapperContainer}>
          <Header />
          <Box component='main' className={classes.contentBoxContainer}>
            <Suspense fallback={<Loader />}>
              <Outlet />
            </Suspense>
          </Box>
        </Box>
      </ThemeProvider>
    </StyledEngineProvider>
  )
}

export default Wrapper
